import { LocationParams } from '../pages/[...vertical]';
import { SupplierSection, SupplierData } from '../types';
import { convertVertical } from './formatting';
import {
  getLocalisedSupplierCount,
  getSuppliers,
  GetSuppliersParams,
} from './suppliers';
import { MAX_SUPPLIERS_V2 } from '../constants/suppliers';
import { captureError } from './monitoring';

interface GetSupplierForVerticalArgsV2 {
  verticalName: string;
  supplierData: SupplierData;
  locationParams: LocationParams | {};
  limit?: number;
  // array of supplier UUIDs
  exclude?: string[];
}

export const getSuppliersForVerticalV2 = async ({
  verticalName,
  supplierData,
  locationParams,
  limit = MAX_SUPPLIERS_V2,
  exclude,
}: GetSupplierForVerticalArgsV2): Promise<SupplierSection> => {
  const [localisedCount, suppliers] = await Promise.all([
    getLocalisedSupplierCount(
      locationParams as Partial<LocationParams>,
      convertVertical(supplierData.vertical1)
    ).catch(error => {
      captureError(error, { source: 'getLocalisedSupplierCount' });
      return null;
    }),
    getSuppliersWithVariablesV2({
      verticalName,
      supplierData,
      locationParams,
      limit,
      exclude,
    }),
  ]);

  // A failed count shouldn't take the page down, fall back to what the search returned
  const count = localisedCount ?? suppliers.results.length;

  return {
    count,
    outOfRange: count ? false : true,
    groups: [{ results: suppliers.results }],
    results: suppliers.results,
  };
};

export const getSuppliersWithVariablesV2 = async ({
  supplierData,
  locationParams,
  limit = MAX_SUPPLIERS_V2,
  exclude,
}: {
  verticalName: string;
  supplierData: SupplierData;
  locationParams: LocationParams | {};
  limit: number;
  exclude?: string[];
}) => {
  const hasLocation = locationParams && 'lat' in locationParams;

  const {
    vertical1,
    vertical2,
    tags: mandatoryTags,
    tagsFilter,
  } = supplierData;

  const filters: GetSuppliersParams = {
    tags: tagsFilter,
    mandatoryTags,
    tier1: convertVertical(vertical1),
    tier2: vertical2?.map(convertVertical) || [],
    limit,
    exclude,
    ...locationParams,
  };

  return getSuppliers(filters)
    .then(async ({ results, count: totalCount }) => {
      let count = totalCount || results.length;

      // Fallback: if fewer than MAX_SUPPLIERS_V2 results and we originally had location, fetch generic list without location
      if (hasLocation && results.length < MAX_SUPPLIERS_V2) {
        // Same filters without the location or distance sort
        const genericFilters: GetSuppliersParams = {
          ...filters,
          lat: undefined,
          lon: undefined,
          sort: undefined,
        };

        try {
          const { results: genericResults } = await getSuppliers(
            genericFilters
          );

          // Merge, avoiding duplicates by uuid
          const existingIds = new Set(results.map(r => r.uuid));
          genericResults.forEach(r => {
            if (!existingIds.has(r.uuid) && results.length < MAX_SUPPLIERS_V2) {
              results.push(r);
            }
          });

          count = results.length;
        } catch (err) {
          return {
            results: [],
            count: 0,
          };
        }
      }

      return {
        results,
        count,
      };
    })
    .catch(() => ({
      results: [],
      count: 0,
    }));
};
